import { format, parse } from "date-fns";
import toast from "react-hot-toast";
import i18n from "../../../../../i18n";
import { AppDispatch, RootState } from "../../../../../store/store";
import { selectAuthUser } from "../../../../auth/redux/authSlice";
import {
  createTicketExportList,
  createTicketExport,
  getExportStatus,
  downloadExportFile,
  type ExportStatusResponse,
  type CreateTicketExportParams
} from "../../api/TicketExportApi";
import {
  exportRequested,
  exportCreated,
  exportResumed,
  exportStatusUpdated,
  type ExportKind,
  type TicketExportStatus
} from "./slice";
import {
  savePendingExport,
  readPendingExport,
  clearPendingExport
} from "./localStorage";
import type { TicketRequestsFilters } from "../../api/RequestReportApi";
import type { ExportProgressReportValues } from "../../components/request-report/ExportProgressReportModal";
import { createTaskExport } from "../../api/MissionReportApi";
import type { MissionReportFilterValues } from "../../types/report.types";

const POLL_INTERVAL_MS = 3000;
const TOTAL_TIMEOUT_MS = 3 * 60 * 1000;
const MAX_STATUS_CHECK_RETRIES = 3;

function getExportKindMessages(kind: ExportKind) {
  return {
    success: i18n.t(`report:export.${kind}.success`),
    downloadFailure: i18n.t(`report:export.${kind}.downloadFailure`),
    genericFailure: i18n.t(`report:export.${kind}.genericFailure`),
    timeoutFailure: i18n.t(`report:export.${kind}.timeoutFailure`)
  };
}

function mapApiStatus(
  status: ExportStatusResponse["status"]
): TicketExportStatus {
  switch (status) {
    case 1:
      return "pending";
    case 2:
      return "processing";
    case 5:
      return "uploading";
    case 3:
      return "completed";
    case 4:
    case 6:
    default:
      return "failed";
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const apiMessage = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return apiMessage || fallback;
}

function toApiDateTime(value: string, endOfDay: boolean): string {
  const parsed = parse(value, "dd/MM/yyyy", new Date());
  return format(parsed, `yyyy-MM-dd ${endOfDay ? "23:59:59" : "00:00:00"}`);
}

function resolveUserId(getState: () => RootState): number | null {
  return selectAuthUser(getState())?.id ?? null;
}

async function pollUntilDone(
  dispatch: AppDispatch,
  getState: () => RootState,
  kind: ExportKind,
  userId: number,
  exportId: number,
  startedAt: number,
  statusCheckFailures = 0
): Promise<void> {
  if (resolveUserId(getState) !== userId) return;

  const messages = getExportKindMessages(kind);
  let response: ExportStatusResponse;
  try {
    response = await getExportStatus(exportId);
  } catch {
    if (resolveUserId(getState) !== userId) return;
    if (statusCheckFailures + 1 < MAX_STATUS_CHECK_RETRIES) {
      await wait(POLL_INTERVAL_MS);
      await pollUntilDone(
        dispatch,
        getState,
        kind,
        userId,
        exportId,
        startedAt,
        statusCheckFailures + 1
      );
      return;
    }
    clearPendingExport(kind, userId);
    dispatch(
      exportStatusUpdated({
        kind,
        status: "failed",
        error: i18n.t("report:export.statusCheckFailure")
      })
    );
    toast.error(messages.genericFailure);
    return;
  }

  if (resolveUserId(getState) !== userId) return;

  const status = mapApiStatus(response.status);

  if (status === "completed") {
    clearPendingExport(kind, userId);
    if (!response.file_content) {
      dispatch(
        exportStatusUpdated({
          kind,
          status: "failed",
          error: messages.downloadFailure
        })
      );
      toast.error(messages.downloadFailure);
      return;
    }
    dispatch(exportStatusUpdated({ kind, status: "completed" }));
    try {
      await downloadExportFile(response);
      if (resolveUserId(getState) !== userId) return;
      toast.success(messages.success);
    } catch {
      if (resolveUserId(getState) !== userId) return;
      dispatch(
        exportStatusUpdated({
          kind,
          status: "failed",
          error: messages.downloadFailure
        })
      );
      toast.error(messages.downloadFailure);
    }
    return;
  }

  if (status === "failed") {
    clearPendingExport(kind, userId);
    const message = response.error ?? messages.genericFailure;
    dispatch(exportStatusUpdated({ kind, status: "failed", error: message }));
    toast.error(message);
    return;
  }

  if (Date.now() - startedAt > TOTAL_TIMEOUT_MS) {
    clearPendingExport(kind, userId);
    dispatch(
      exportStatusUpdated({
        kind,
        status: "failed",
        error: messages.timeoutFailure
      })
    );
    toast.error(messages.timeoutFailure);
    return;
  }

  dispatch(exportStatusUpdated({ kind, status }));
  await wait(POLL_INTERVAL_MS);
  await pollUntilDone(dispatch, getState, kind, userId, exportId, startedAt);
}

export function triggerTicketExport(filters: TicketRequestsFilters) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const kind: ExportKind = "list";
    const userId = resolveUserId(getState);
    if (userId == null) {
      dispatch(
        exportStatusUpdated({
          kind,
          status: "failed",
          error: i18n.t("report:export.createFailure")
        })
      );
      toast.error(getExportKindMessages(kind).genericFailure);
      return;
    }

    dispatch(exportRequested({ kind }));
    toast(i18n.t("report:export.preparingData"));

    let response: ExportStatusResponse;
    try {
      response = await createTicketExportList(filters);
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        getExportKindMessages(kind).genericFailure
      );
      dispatch(exportStatusUpdated({ kind, status: "failed", error: message }));
      toast.error(message);
      return;
    }

    const startedAt = Date.now();
    dispatch(exportCreated({ kind, exportId: response.id, startedAt }));
    savePendingExport(kind, userId, { exportId: response.id, startedAt });
    await pollUntilDone(dispatch, getState, kind, userId, response.id, startedAt);
  };
}

export function triggerProgressReportExport(
  values: ExportProgressReportValues
) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const kind: ExportKind = "progressReport";
    const userId = resolveUserId(getState);
    if (userId == null) {
      dispatch(
        exportStatusUpdated({
          kind,
          status: "failed",
          error: i18n.t("report:export.createFailure")
        })
      );
      toast.error(getExportKindMessages(kind).genericFailure);
      return;
    }

    dispatch(exportRequested({ kind }));
    toast(i18n.t("report:export.preparingData"));

    let response: ExportStatusResponse;
    try {
      const params: CreateTicketExportParams = {
        processIds: (values.processIds ?? []).map(Number),
        startDate: toApiDateTime(values.startDate as string, false),
        endDate: toApiDateTime(values.endDate as string, true)
      };
      response = await createTicketExport(params);
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        getExportKindMessages(kind).genericFailure
      );
      dispatch(exportStatusUpdated({ kind, status: "failed", error: message }));
      toast.error(message);
      return;
    }

    const startedAt = Date.now();
    dispatch(exportCreated({ kind, exportId: response.id, startedAt }));
    savePendingExport(kind, userId, { exportId: response.id, startedAt });
    await pollUntilDone(dispatch, getState, kind, userId, response.id, startedAt);
  };
}

export function triggerTaskExport(filters: Partial<MissionReportFilterValues>) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const kind: ExportKind = "missionList";
    const userId = resolveUserId(getState);
    if (userId == null) {
      dispatch(
        exportStatusUpdated({
          kind,
          status: "failed",
          error: i18n.t("report:export.createFailure")
        })
      );
      toast.error(getExportKindMessages(kind).genericFailure);
      return;
    }

    dispatch(exportRequested({ kind }));
    toast(i18n.t("report:export.preparingData"));

    let response: ExportStatusResponse;
    try {
      response = await createTaskExport(filters);
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        getExportKindMessages(kind).genericFailure
      );
      dispatch(exportStatusUpdated({ kind, status: "failed", error: message }));
      toast.error(message);
      return;
    }

    const startedAt = Date.now();
    dispatch(exportCreated({ kind, exportId: response.id, startedAt }));
    savePendingExport(kind, userId, { exportId: response.id, startedAt });
    await pollUntilDone(dispatch, getState, kind, userId, response.id, startedAt);
  };
}

const activeResumes = new Set<string>();

export function resumeExport(kind: ExportKind) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const userId = resolveUserId(getState);
    if (userId == null) return;

    const resumeKey = `${userId}:${kind}`;
    if (activeResumes.has(resumeKey)) return;

    const pending = readPendingExport(kind, userId);
    if (!pending) return;

    activeResumes.add(resumeKey);
    try {
      dispatch(
        exportResumed({
          kind,
          exportId: pending.exportId,
          startedAt: pending.startedAt
        })
      );
      await pollUntilDone(
        dispatch,
        getState,
        kind,
        userId,
        pending.exportId,
        pending.startedAt
      );
    } finally {
      activeResumes.delete(resumeKey);
    }
  };
}
